import React, { memo } from 'react';

import Error from 'next/error';

import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { withCtxMiddleware } from '@bonnet/next/ctx-middleware';
import { withCtxAbortOnStatusCode } from '@bonnet/next/ctx-middleware/with-ctx-abort-on-status-code';
import { withBonnetPage } from '@bonnet/next/page';

import { pageNames } from '@atc/bonnet-paths';
import { useFeatures } from '@atc/features';
import { getScaledImageSource, imageSizes } from '@atc/images';

import { StickyContainer } from 'reaxl';

import getSRPPrimaryImagesFromCtx from '@/utilities/getSRPPrimaryImagesFromCtx';
import usePauseAudioEye from '@/utilities/usePauseAudioEye';

import campaignConfigData from '@/config/campaignConfigs';

import addSearchResultsPageData from '@/context-middleware/addSearchResultsPageData.js';
import ctxPresetFindCar from '@/context-middleware/ctxPresetFindCar.js';
import dispatchSearchResultsPageData from '@/context-middleware/dispatchSearchResultsPageData.js';
import srpFetchCompositeJSON from '@/context-middleware/srp/srpFetchCompositeJSON';
import withBreadcrumbPaths from '@/context-middleware/srp/withBreadcrumbPaths';
import withCampaignCookie from '@/context-middleware/srp/withCampaignCookie';
import withKbbEditorialFetcher from '@/context-middleware/srp/withKbbEditorialFetcher';
import withPrivateSellerRadiusBoost from '@/context-middleware/srp/withPrivateSellerRadiusBoost';
import withRequestOptions from '@/context-middleware/srp/withRequestOptions';
import withSavedRecentSearch from '@/context-middleware/srp/withSavedRecentSearch';
import withSEOContent from '@/context-middleware/srp/withSEOContent';
import withSortQuery from '@/context-middleware/srp/withSortQuery';
import withSrpQueryFixes from '@/context-middleware/srp/withSrpQueryFixes';
import withCustomLeadRouting from '@/context-middleware/withCustomLeadRouting.js';
import withDataIslandDispatcher from '@/context-middleware/withDataIslandDispatcher.js';
import withInterestRates from '@/context-middleware/withInterestRates.js';
import withPreloadHeader from '@/context-middleware/withPreloadHeader.js';
import withSelfServiceSeo from '@/context-middleware/withSelfServiceSeo.js';
import withWalletConfigs from '@/context-middleware/withWalletConfigs.js';

import listingsAgentDuck from '@/ducks/listingsAgentDuck';
import srpActiveEmailListingDuck from '@/ducks/srp/srpActiveEmailListingDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpAdditionalCrawlPathsDuck from '@/ducks/srp/srpAdditionalCrawlPathsDuck.js';
import srpAdsDuck from '@/ducks/srp/srpAdsDuck.js';
import srpBreadcrumbsDuck from '@/ducks/srp/srpBreadcrumbsDuck.js';
import srpCitiesCrawlPathsDuck from '@/ducks/srp/srpCitiesCrawlPathsDuck.js';
import srpContentDuck from '@/ducks/srp/srpContentDuck.js';
import srpCrawlPathsDataMapsDuck from '@/ducks/srp/srpCrawlPathsDataMapsDuck.js';
import srpDealershipsCrawlPathsDuck from '@/ducks/srp/srpDealershipsCrawlPathsDuck.js';
import srpEditorialReviewsDuck from '@/ducks/srp/srpEditorialReviewsDuck.js';
import srpExpiredListingMessageDuck from '@/ducks/srp/srpExpiredListingMessageDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpFixedAdDuck from '@/ducks/srp/srpFixedAdDuck.js';
import srpInventoryDuck from '@/ducks/srp/srpInventoryDuck.js';
import srpListingTypesCrawlPathsDuck from '@/ducks/srp/srpListingTypesCrawlPathsDuck.js';
import srpLocationTypeDuck from '@/ducks/srp/srpLocationTypeDuck.js';
import srpMakeCrawlPathsDuck from '@/ducks/srp/srpMakeCrawlPathsDuck.js';
import srpMarketExtensionDuck from '@/ducks/srp/srpMarketExtensionDuck.js';
import srpModelCrawlPathsDuck from '@/ducks/srp/srpModelCrawlPathsDuck.js';
import srpNewCarBoostDuck from '@/ducks/srp/srpNewCarBoostDuck.js';
import srpNewSearchDuck from '@/ducks/srp/srpNewSearchDuck.js';
import srpNoSearchResultsDuck from '@/ducks/srp/srpNoSearchResultsDuck.js';
import srpPaginationDuck from '@/ducks/srp/srpPaginationDuck.js';
import srpPrimeSpotlightDuck from '@/ducks/srp/srpPrimeSpotlightDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpReuseActiveFourthSpotlightDuck from '@/ducks/srp/srpReuseActiveFourthSpotlightDuck.js';
import srpReuseActiveStateDuck from '@/ducks/srp/srpReuseActiveStateDuck.js';
import srpSelectedFiltersInOrderDuck from '@/ducks/srp/srpSelectedFiltersInOrderDuck.js';
import srpSEOMileageDuck from '@/ducks/srp/srpSEOMileageDuck.js';
import srpSEOPricingDuck from '@/ducks/srp/srpSEOPricingDuck.js';
import srpSimilarListingsDuck from '@/ducks/srp/srpSimilarListingsDuck.js';
import srpSortDuck from '@/ducks/srp/srpSortDuck.js';
import srpSponsorshipDuck from '@/ducks/srp/srpSponsorshipDuck.js';
import srpSpotlightDuck from '@/ducks/srp/srpSpotlightDuck.js';
import srpStrongAttributesDuck from '@/ducks/srp/srpStrongAttributesDuck.js';
import srpSuggestedFiltersDuck from '@/ducks/srp/srpSuggestedFiltersDuck.js';
import srpSuggestedMakeModelValuesDuck from '@/ducks/srp/srpSuggestedMakeModelValuesDuck.js';
import srpSupplementalDuck from '@/ducks/srp/srpSupplementalDuck.js';
import srpTrimCrawlPathsDuck from '@/ducks/srp/srpTrimCrawlPathsDuck';
import srpYearCrawlPathsDuck from '@/ducks/srp/srpYearCrawlPathsDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpDealerDiffDuck from '@/ducks/vdp/vdpDealerDiffDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import AlphaModule from '@/modules/AlphaModule.js';
import atcAuthModule from '@/modules/AuthModule.js';
import CanonicalUrlModule from '@/modules/CanonicalUrlModule.js';
import CookieConsentModule from '@/modules/CookieConsentModule.js';
import ParsedQueryModule from '@/modules/ParsedQueryModule.js';
import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule.js';

import CommonHeadContainer from '@/containers/CommonHeadContainer';
import PersonalizationEngineContainer from '@/containers/PersonalizationEngineContainer';
import SearchResultsPageContainer from '@/containers/srp/SearchResultsPageContainer';
import SRPAnalytics from '@/containers/srp/SRPAnalytics';
import SrpPageEventsContainer from '@/containers/srp/SrpPageEventsContainer';

import JumpLink from '@/components/JumpLink/index.js';
import SrpError from '@/components/SrpError/index.js';

const srpDucks = {
    srpActiveInteractionDuck,
    srpActiveEmailListingDuck,
    srpAdditionalCrawlPathsDuck,
    srpAdsDuck,
    srpBreadcrumbsDuck,
    srpCitiesCrawlPathsDuck,
    srpContentDuck,
    srpCrawlPathsDataMapsDuck,
    srpDealershipsCrawlPathsDuck,
    srpEditorialReviewsDuck,
    srpExpiredListingMessageDuck,
    srpFiltersDuck,
    srpFixedAdDuck,
    srpInventoryDuck,
    srpListingTypesCrawlPathsDuck,
    srpMarketExtensionDuck,
    srpModelCrawlPathsDuck,
    srpMakeCrawlPathsDuck,
    srpNewCarBoostDuck,
    srpNewSearchDuck,
    srpNoSearchResultsDuck,
    srpPaginationDuck,
    srpSEOPricingDuck,
    srpSEOMileageDuck,
    srpResultsDuck,
    srpSimilarListingsDuck,
    srpSortDuck,
    srpSponsorshipDuck,
    srpSpotlightDuck,
    srpPrimeSpotlightDuck,
    srpSupplementalDuck,
    srpSuggestedMakeModelValuesDuck,
    srpSuggestedFiltersDuck,
    srpTrimCrawlPathsDuck,
    srpYearCrawlPathsDuck,
    srpReuseActiveFourthSpotlightDuck,
    srpReuseActiveStateDuck,
    srpSelectedFiltersInOrderDuck,
    srpStrongAttributesDuck,
    srpLocationTypeDuck,
};

const { SEARCH_RESULTS } = pageNames;

const SearchResultsPage = memo(({
    openGraphImage,
    currentUrl,
    statusCode,
    showSrpError = false,
    query = {},
    totalBoostCount,
}) => {
    const {
        personalization_engine: [enablePersonalizationEngine],
    } = useFeatures([
        'personalization_engine',
    ]);

    usePauseAudioEye();

    // if the srp has error from a fallback api call
    // then show an SrpError that allows the user to try again
    if (showSrpError) {
        return (
            <SrpError />
        );
    }

    if (statusCode) {
        return <Error statusCode={statusCode} />;
    }

    return (
        <>
            {enablePersonalizationEngine && <PersonalizationEngineContainer pageName={SEARCH_RESULTS} />}
            <CommonHeadContainer
                currentUrl={currentUrl}
                pageName={SEARCH_RESULTS}
                query={query}
                openGraphImage={openGraphImage}
            />
            <SRPAnalytics totalBoostCount={totalBoostCount} />
            <SrpPageEventsContainer />
            <JumpLink
                message="Skip to vehicle Listings"
                id="#srp-listings"
            />
            <StickyContainer>
                <SearchResultsPageContainer currentUrl={currentUrl} />
            </StickyContainer>
        </>
    );
}, _isEqual);

SearchResultsPage.getInitialProps = async (ctx) => {

    // apply context middleware
    await withCtxMiddleware([
        ctxPresetFindCar({
            pageName: SEARCH_RESULTS,
            preFetchCtxMiddleware: [
                withSrpQueryFixes(),
                withSelfServiceSeo(),
                withCampaignCookie({ data: campaignConfigData }),
                withPrivateSellerRadiusBoost(),
                withRequestOptions(),
                withKbbEditorialFetcher(),
                ctx.withModuleCtxMiddleware({
                    batchDispatch: false,
                    fetchCompositeJSON: false,
                }),
                // AlphaModule.ctxMiddleware,
                withSEOContent(),
                addSearchResultsPageData(),
            ],
            fetcher: srpFetchCompositeJSON,
            dispatcher: dispatchSearchResultsPageData,
        }),
        withCtxAbortOnStatusCode(),
        withBreadcrumbPaths(),
        withInterestRates(),
        withWalletConfigs(),
        withCustomLeadRouting(),
        withSavedRecentSearch(),
        withPreloadHeader(),
        withDataIslandDispatcher(),
        withSortQuery(),
    ], ctx);

    if (ctx.data.showSrpError) {
        if (ctx.data.isBot) {
            ctx.res?.status(200);
            ctx.data.showSrpError = false;
        } else {
            ctx.res?.status(500);
        }
        return {
            showSrpError: ctx.data.showSrpError,
        };
    }

    // if the current ctx contains a status code then we need return it
    if (ctx.statusCode) {
        return {
            statusCode: ctx.statusCode,
        };
    }

    const images = getSRPPrimaryImagesFromCtx(ctx);

    let openGraphImage;
    if (ctx?.data?.listings?.length > 0) {
        const primary = _get(images, 'primary', 0);
        const rawOpenGraphImage = _get(ctx, `data.listings[0].images.sources[${primary}].src`);
        openGraphImage = rawOpenGraphImage && getScaledImageSource({
            src: rawOpenGraphImage,
            ...imageSizes.openGraphImg,
        });
    }
    const totalBoostCount = ctx?.data?.totalBoostCount;

    return {
        images,
        openGraphImage,
        query: ctx.query,
        currentUrl: ctx.data.currentUrl,
        totalBoostCount,
    };
};

const { BonnetPageComponent } = withBonnetPage(SearchResultsPage, {
    reducers: [
        ...Object.values(srpDucks),
        listingsAgentDuck,
        vdpResultsDuck,
        vdpActiveInteractionDuck,
        vdpDealerDiffDuck,
    ],
    modules: [
        AlphaModule,
        CanonicalUrlModule,
        CookieConsentModule,
        ParsedQueryModule,
        atcAuthModule,
        PersonalizationEngineModule,
    ],
});

export default BonnetPageComponent;
